import { Partners } from '@lockton-apps/shared/interfaces';

import { pncQuotesTablePreset } from './dynamic-tables-pnc';
import {
  cancellationsTablePreset,
  policiesTablePreset,
  principalsTablePreset,
  quotesTablePreset,
} from './dynamic-tables-default';
import { get, has } from 'lodash';

import { DynamicTableConfig } from '../interfaces/dynamic-table.interface';
import {
  accaDashboardQuotesTablePreset,
  accaQuotesTablePreset,
} from './dynamic-table-acca';
import {
  norwayDashboardQuotesTablePreset,
  norwayQuotesTablePreset,
} from './dynamic-table-norway';
import {
  mmpiDashboardQuotesTablePreset,
  mmpiQuotesTablePreset,
} from './dynamic-table-mmpi';
import {
  lsosClientEnquiriesQuotesTablePreset,
  lsosQuotesTablePreset,
} from './dynamic-table-lsos';
import {
  QUOTES_BROKER_CONFIG_CANCELLATIONS,
  QUOTES_BROKER_CONFIG_DEFAULT,
  QUOTES_BROKER_CONFIG_POLICIES,
} from '../constants/registry/quotes-table.constant';
import { dynamicTableBuilder } from '../utils/dynamic-table.util';
import { PRINCIPALS_BROKER_CONFIG_DEFAULT } from '../constants/registry/principals-table.constant';

const dynamicTables = {
  quotes: dynamicTableBuilder(QUOTES_BROKER_CONFIG_DEFAULT),
  policies: dynamicTableBuilder(QUOTES_BROKER_CONFIG_POLICIES),
  cancellations: dynamicTableBuilder(QUOTES_BROKER_CONFIG_CANCELLATIONS),
  principals: dynamicTableBuilder(PRINCIPALS_BROKER_CONFIG_DEFAULT),
};

export enum DynamicTablePresetKeys {
  Quotes = 'quotes',
  Cancellations = 'cancellations',
  Policies = 'policies',
  Principals = 'principals',
}

export const dynamicTablePartnerPresets = {
  [Partners.LSOS]: {
    [DynamicTablePresetKeys.Quotes]: {
      // TODO: update when LSOS is out of experimental
      DEFAULT: dynamicTables.quotes(lsosQuotesTablePreset),
      ClientEnquiries: dynamicTables.quotes(
        lsosClientEnquiriesQuotesTablePreset
      ),
    },
  },
  [Partners.PNC]: {
    [DynamicTablePresetKeys.Quotes]: {
      DEFAULT: dynamicTables.quotes(pncQuotesTablePreset),
    },
  },
  [Partners.ACCA]: {
    [DynamicTablePresetKeys.Quotes]: {
      DEFAULT: dynamicTables.quotes(accaDashboardQuotesTablePreset),
      ClientEnquiries: dynamicTables.quotes(accaQuotesTablePreset),
    },
  },
  [Partners.NORWAY]: {
    [DynamicTablePresetKeys.Quotes]: {
      DEFAULT: dynamicTables.quotes(norwayDashboardQuotesTablePreset),
      ClientEnquiries: dynamicTables.quotes(norwayQuotesTablePreset),
    },
  },
  [Partners.NORWAY_PI]: {
    [DynamicTablePresetKeys.Quotes]: {
      DEFAULT: dynamicTables.quotes(norwayDashboardQuotesTablePreset),
      ClientEnquiries: dynamicTables.quotes(norwayQuotesTablePreset),
    },
  },
  [Partners.ENW]: {
    [DynamicTablePresetKeys.Quotes]: {
      DEFAULT: dynamicTables.quotes(mmpiDashboardQuotesTablePreset),
      ClientEnquiries: dynamicTables.quotes(mmpiQuotesTablePreset),
    },
  },
  [Partners.CONSTRUCTION]: {
    [DynamicTablePresetKeys.Quotes]: {
      DEFAULT: dynamicTables.quotes(mmpiDashboardQuotesTablePreset),
      ClientEnquiries: dynamicTables.quotes(mmpiQuotesTablePreset),
    },
  },
  [Partners.REGULATORY]: {
    [DynamicTablePresetKeys.Quotes]: {
      DEFAULT: dynamicTables.quotes(mmpiDashboardQuotesTablePreset),
      ClientEnquiries: dynamicTables.quotes(mmpiQuotesTablePreset),
    },
  },
  [Partners.ACCA_OVER]: {
    [DynamicTablePresetKeys.Quotes]: {
      DEFAULT: dynamicTables.quotes(mmpiDashboardQuotesTablePreset),
      ClientEnquiries: dynamicTables.quotes(mmpiQuotesTablePreset),
    },
  },
  DEFAULT: {
    [DynamicTablePresetKeys.Policies]: {
      DEFAULT: dynamicTables.policies(policiesTablePreset),
    },
    [DynamicTablePresetKeys.Quotes]: {
      DEFAULT: dynamicTables.quotes(quotesTablePreset),
    },
    [DynamicTablePresetKeys.Cancellations]: {
      DEFAULT: dynamicTables.cancellations(cancellationsTablePreset),
    },
    [DynamicTablePresetKeys.Principals]: {
      DEFAULT: dynamicTables.principals(principalsTablePreset),
    },
  },
};

export function getPartnerTablePreset(
  presetKey: DynamicTablePresetKeys,
  partnerId: number | 'DEFAULT' | undefined = 'DEFAULT',
  variant: string | undefined = 'DEFAULT'
): DynamicTableConfig {
  const queryPartnerKey = has(dynamicTablePartnerPresets, [
    partnerId,
    presetKey,
  ])
    ? partnerId
    : 'DEFAULT';

  const variantKey = has(dynamicTablePartnerPresets, [
    queryPartnerKey,
    presetKey,
    variant,
  ])
    ? variant
    : 'DEFAULT';

  return get(dynamicTablePartnerPresets, [
    queryPartnerKey,
    presetKey,
    variantKey,
  ]);
}
